import {
  useLayoutEffect,
  useRef,
  type ComponentPropsWithoutRef,
  type ReactNode,
  type Ref,
} from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, ArrowUpRight, Play } from 'lucide-react'
import { magnetic } from '../../animations/gsapAnimations'

type Variant = 'primary' | 'ghost' | 'link'
type Size = 'sm' | 'md' | 'lg'
type Icon = 'arrow' | 'up-right' | 'play'

const BASE =
  'inline-flex items-center justify-center gap-3 font-semibold tracking-[-0.01em] transition duration-300 ease-lux disabled:cursor-not-allowed disabled:opacity-55'

/* Pills, like everything else a hand touches here. The primary is the one
   piece of lit material on the page — lime with light falling from above
   (see btn-lime), black type, and a single pass of sheen across it on hover.
   The ghost is the outline reading: a lime hairline on black that fills a
   shade when approached. */
const VARIANTS: Record<Variant, string> = {
  primary:
    'btn-sheen btn-lime rounded-full text-void shadow-action hover:shadow-pop active:translate-y-px',
  ghost:
    'rounded-full border border-lime/55 bg-transparent text-platinum hover:border-lime hover:bg-lime/10 active:translate-y-px',
  /* Not a button shape at all — a text action with an arrow that leads. */
  link: 'text-lime hover:text-lime-lift [&_svg]:transition-transform [&_svg]:duration-200 hover:[&_svg]:translate-x-1',
}

const SIZES: Record<Size, string> = {
  sm: 'h-10 px-5 text-[12.5px]',
  md: 'h-12 px-6 text-[13.5px]',
  lg: 'h-14 px-7 text-[14.5px]',
}

/* With a badge on the right the pill's own padding closes up so the disc
   sits inside the edge, the way the reference draws it. */
const BADGED: Record<Size, string> = {
  sm: 'pr-1.5',
  md: 'pr-1.5',
  lg: 'pr-2',
}

const BADGE_SIZE: Record<Size, string> = {
  sm: 'size-7 [&_svg]:size-3.5',
  md: 'size-9 [&_svg]:size-4',
  lg: 'size-10 [&_svg]:size-4',
}

/* The disc the icon rides in: black on a lime button, lime-lined on a
   ghost — each is the other's inverse. */
const BADGE: Record<Exclude<Variant, 'link'>, string> = {
  primary:
    'grid shrink-0 place-items-center rounded-full bg-void text-lime transition-transform duration-300 group-hover/btn:translate-x-0.5',
  ghost:
    'grid shrink-0 place-items-center rounded-full border border-lime text-lime transition-transform duration-300 group-hover/btn:translate-x-0.5',
}

const ICONS: Record<Icon, typeof ArrowRight> = {
  arrow: ArrowUpRight,
  'up-right': ArrowUpRight,
  play: Play,
}

type CommonProps = {
  children: ReactNode
  variant?: Variant
  size?: Size
  /** Appends the icon disc. Implied (as a plain arrow) by variant="link". */
  arrow?: boolean
  /** Which glyph the disc carries. Defaults to the up-right arrow. */
  icon?: Icon
  /** Leans toward a fine pointer. For the one primary action in a view. */
  magnetic?: boolean
  className?: string
}

type ButtonAsButton = CommonProps &
  Omit<ComponentPropsWithoutRef<'button'>, keyof CommonProps> & {
    to?: never
    href?: never
  }

type ButtonAsLink = CommonProps &
  Omit<ComponentPropsWithoutRef<'a'>, keyof CommonProps | 'href'> & {
    /** An in-app route — renders a react-router <Link>. */
    to: string
    href?: never
  }

type ButtonAsAnchor = CommonProps &
  Omit<ComponentPropsWithoutRef<'a'>, keyof CommonProps> & {
    /** An external URL, tel: or wa.me link — renders a plain <a>. */
    href: string
    to?: never
  }

type ButtonProps = ButtonAsButton | ButtonAsLink | ButtonAsAnchor

/* One button, three jobs. Which element it renders is decided by the props
   it is given, so a CTA that navigates is never a <button> with an onClick
   handler faking a link. */
function Button(props: ButtonProps) {
  const {
    children,
    variant = 'primary',
    size = 'md',
    arrow = false,
    icon = 'arrow',
    magnetic: pulls = false,
    className = '',
    ...rest
  } = props

  /* The pull is a listener pair on the element, wired after mount and
     removed with it. Off by default: two magnetic buttons side by side
     lean toward the same cursor, and the pair reads as a twitch. */
  const ref = useRef<HTMLElement>(null)
  useLayoutEffect(() => {
    const el = ref.current
    if (!el || !pulls) return
    return magnetic(el)
  }, [pulls])

  const badged = arrow && variant !== 'link'
  const shape =
    variant === 'link'
      ? SIZES[size].split(' ').pop()
      : `${SIZES[size]} ${badged ? BADGED[size] : ''}`
  const classes = `group/btn ${BASE} ${VARIANTS[variant]} ${shape} ${className}`

  const Glyph = ICONS[icon]

  const content = (
    <>
      {children}
      {variant === 'link' ? (
        <ArrowRight aria-hidden="true" className="size-4" strokeWidth={1.9} />
      ) : badged ? (
        <span aria-hidden="true" className={`${BADGE[variant]} ${BADGE_SIZE[size]}`}>
          <Glyph
            strokeWidth={icon === 'play' ? 0 : 2.2}
            fill={icon === 'play' ? 'currentColor' : 'none'}
            className={icon === 'play' ? 'ml-0.5' : ''}
          />
        </span>
      ) : null}
    </>
  )

  if ('to' in rest && rest.to !== undefined) {
    const { to, ...anchorProps } = rest as ButtonAsLink
    return (
      <Link
        ref={ref as Ref<HTMLAnchorElement>}
        to={to}
        className={classes}
        {...anchorProps}
      >
        {content}
      </Link>
    )
  }

  if ('href' in rest && rest.href !== undefined) {
    return (
      <a
        ref={ref as Ref<HTMLAnchorElement>}
        className={classes}
        {...(rest as ButtonAsAnchor)}
      >
        {content}
      </a>
    )
  }

  return (
    <button
      ref={ref as Ref<HTMLButtonElement>}
      type="button"
      className={classes}
      {...(rest as ComponentPropsWithoutRef<'button'>)}
    >
      {content}
    </button>
  )
}

export default Button
